export function getClientLabel() {
  const ua = navigator.userAgent || '';

  // Browser detection (ordered)
  const browsers = [
    { name: 'Brave', test: () => !!(navigator.brave && navigator.brave.isBrave !== undefined), version: /Chrome\/([0-9.]+)/ },
    { name: 'Edge', test: /Edg(e|A|iOS)?\//, version: /Edg(?:e|A|iOS)?\/([0-9.]+)/ },
    { name: 'Opera', test: /OPR\/|Opera/, version: /(?:OPR|Opera)\/([0-9.]+)/ },
    { name: 'Vivaldi', test: /Vivaldi/, version: /Vivaldi\/([0-9.]+)/ },
    { name: 'Firefox', test: /Firefox\/|FxiOS/, version: /Firefox\/([0-9.]+)/ },
    { name: 'Safari', test: () => /Safari/.test(ua) && !/Chrome|Chromium|CriOS|OPR|Edg/.test(ua), version: /Version\/([0-9.]+)/ },
    { name: 'Chrome', test: /Chrome\/|CriOS\//, version: /Chrome\/([0-9.]+)|CriOS\/([0-9.]+)/ },
  ];

  function detectBrowser() {
    for (const b of browsers) {
      const match = (typeof b.test === 'function') ? b.test() : b.test.test(ua);
      if (match) {
        const m = ua.match(b.version);
        const version = m ? (m[1] || m[2] || '') : '';
        return version ? `${b.name} ${version.split('.')[0]}` : b.name;
      }
    }
    return null;
  }

  function detectOS() {
    if (/windows phone|windows mobile/i.test(ua)) return 'Windows Phone';
    if (/windows nt 10.0/i.test(ua)) return 'Windows 10';
    if (/windows nt 6.3/i.test(ua)) return 'Windows 8.1';
    if (/windows nt 6.2/i.test(ua)) return 'Windows 8';
    if (/windows nt 6.1/i.test(ua)) return 'Windows 7';
    if (/mac os x/i.test(ua)) {
      const m = ua.match(/Mac OS X ([0-9_\.]+)/);
      return m ? `macOS ${m[1].replace(/_/g, '.')}` : 'macOS';
    }
    if (/android/i.test(ua)) {
      const m = ua.match(/Android\s+([0-9.]+)/i);
      return m ? `Android ${m[1]}` : 'Android';
    }
    if (/\biphone\b|\bipad\b|\bipod\b/i.test(ua)) {
      const m = ua.match(/OS\s([0-9_]+)/);
      return m ? `iOS ${m[1].replace(/_/g, '.')}` : 'iOS';
    }
    if (/linux/i.test(ua)) return 'Linux';
    return null;
  }

  function detectDeviceType() {
    if (/mobile|iphone|ipod|android.*mobile|windows phone/i.test(ua)) return 'mobile';
    if (/tablet|ipad|android(?!.*mobile)|nexus 7|sm-t/i.test(ua)) return 'tablet';
    return 'desktop';
  }

  function detectPhoneModel() {
    if (/\biPhone\b/.test(ua)) {
      // Try to map iPhone model from platform where possible (best-effort via screen size fallback)
      // Preferred: return "iPhone" and try to refine via userAgent tokens like "iPhone11,8"
      const raw = ua.match(/$(.*?)$/);
      if (raw && /iPhone/.test(raw[1])) {
        const modelToken = raw[1].match(/iPhone(?:\s)?[0-9,A-Za-z\-]*/);
        if (modelToken) {
          // UA rarely contains exact marketing model; return "iPhone" with no false specifics
          return 'iPhone';
        }
      }
      // fallback to simply "iPhone"
      return 'iPhone';
    }

    // Android heuristics
    // Common patterns: "SM-G973F", "Pixel 3", "Pixel 3 XL", "HUAWEI P30", "OnePlus A6000", etc.
    // Try tokens inside parentheses after Android version
    const paren = ua.match(/$([^)]+)$/);
    if (paren) {
      const parts = paren[1].split(';').map(p => p.trim());
      // Usually parts contain [Platform, Build info or model]
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        // Skip tokens that are clearly OS or build
        if (/Android|Linux|Build|wv|ARM|x86|U;|\bAppleWebKit\b/i.test(p)) continue;
        // Clean common prefixes
        const cleaned = p.replace(/Build\/.*$/i, '').trim();
        if (cleaned && cleaned.length > 2 && cleaned.length < 60) {
          // Remove tokens that are just browser engine strings
          if (!/Safari|Chrome|wv/i.test(cleaned)) return cleaned;
        }
      }
    }

    // Look for explicit patterns anywhere in UA
    const explicit = ua.match(/\b(Pixel [0-9XL]+|Nexus [0-9]+|SM-[A-Z0-9]+|GT-[A-Z0-9]+|HUAWEI|Huawei|HONOR|OnePlus|OPPO|Vivo|Xiaomi|Mi|Redmi)[- _]?[A-Za-z0-9 ]*/i);
    if (explicit) return explicit[0].trim();

    return null;
  }

  const deviceType = detectDeviceType();
  if (deviceType === 'mobile') {
    const phoneModel = detectPhoneModel();
    if (phoneModel) return phoneModel;
    // as fallback, try manufacturer tokens
    const manu = ua.match(/\b(Samsung|SamsungBrowser|HUAWEI|Huawei|HONOR|OnePlus|OPPO|Vivo|Xiaomi|Mi|Redmi)\b/i);
    if (manu) return manu[1];
    // final fallback: OS or generic mobile
    const os = detectOS();
    return os ? os.split(' ')[0] : 'Mobile';
  }

  // Desktop/tablet: prefer "Browser, OS" or just browser if OS unknown
  const browser = detectBrowser();
  const os = detectOS();
  if (browser && os) return `${os} - ${browser}`;
  if (browser) return browser;
  if (os) return os;
  return 'Unknown';
}
